import { create } from 'zustand';
import {
  archiveSupplier,
  createSupplier,
  getSupplier,
  listSuppliers,
  shareSupplier,
  updateSupplier,
} from '../services/supplier.service';
import type {
  Supplier,
  SupplierInput,
  SupplierPagination,
  SupplierQuery,
  SupplierUpdateInput,
} from '../types/supplier.type';

interface SupplierState {
  suppliers: Supplier[];
  selectedSupplier: Supplier | null;
  total: number;
  page: number;
  limit: number;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  selectedSupplierNotFound: boolean;
  clearError: () => void;
  fetchSuppliers: (query: SupplierQuery) => Promise<SupplierPagination | null>;
  fetchSupplier: (organizationId: string, supplierId: string) => Promise<Supplier | null>;
  createSupplier: (organizationId: string, input: SupplierInput) => Promise<Supplier | null>;
  updateSupplier: (
    organizationId: string,
    supplierId: string,
    input: SupplierUpdateInput,
  ) => Promise<Supplier | null>;
  archiveSupplier: (organizationId: string, supplierId: string) => Promise<boolean>;
  shareSupplier: (
    organizationId: string,
    supplierId: string,
    targetOwnerOrgId: string,
  ) => Promise<Supplier | null>;
}

function errorMessage(error: unknown, fallback: string): string {
  const candidate = error as { response?: { data?: { detail?: string | { message?: string } } } };
  const detail = candidate.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && detail.message) return detail.message;
  return fallback;
}

function isNotFound(error: unknown): boolean {
  return (error as { response?: { status?: number } }).response?.status === 404;
}

export const useSupplierStore = create<SupplierState>((set) => ({
  suppliers: [],
  selectedSupplier: null,
  total: 0,
  page: 1,
  limit: 20,
  isLoading: false,
  isSaving: false,
  error: null,
  selectedSupplierNotFound: false,
  clearError: () => set({ error: null }),

  fetchSuppliers: async (query) => {
    set({ isLoading: true, error: null });
    try {
      const result = await listSuppliers(query);
      set({ ...result, isLoading: false });
      return result;
    } catch (error) {
      if (isNotFound(error)) {
        const emptyResult: SupplierPagination = {
          suppliers: [],
          total: 0,
          page: query.page ?? 1,
          limit: query.limit ?? 20,
        };
        set({ ...emptyResult, isLoading: false, error: null });
        return emptyResult;
      }
      set({ isLoading: false, error: errorMessage(error, 'Failed to load suppliers') });
      return null;
    }
  },

  fetchSupplier: async (organizationId, supplierId) => {
    set({ isLoading: true, error: null, selectedSupplierNotFound: false });
    try {
      const supplier = await getSupplier(organizationId, supplierId);
      set({ selectedSupplier: supplier, isLoading: false, selectedSupplierNotFound: false });
      return supplier;
    } catch (error) {
      if (isNotFound(error)) {
        set({ selectedSupplier: null, isLoading: false, error: null, selectedSupplierNotFound: true });
        return null;
      }
      set({ isLoading: false, error: errorMessage(error, 'Failed to load supplier') });
      return null;
    }
  },

  createSupplier: async (organizationId, input) => {
    set({ isSaving: true, error: null });
    try {
      const supplier = await createSupplier(organizationId, input);
      set((state) => ({ suppliers: [supplier, ...state.suppliers], total: state.total + 1, isSaving: false }));
      return supplier;
    } catch (error) {
      set({ isSaving: false, error: errorMessage(error, 'Failed to create supplier') });
      return null;
    }
  },

  updateSupplier: async (organizationId, supplierId, input) => {
    set({ isSaving: true, error: null });
    try {
      const supplier = await updateSupplier(organizationId, supplierId, input);
      set((state) => ({
        suppliers: state.suppliers.map((row) => row.id === supplierId ? supplier : row),
        selectedSupplier: state.selectedSupplier?.id === supplierId ? supplier : state.selectedSupplier,
        isSaving: false,
      }));
      return supplier;
    } catch (error) {
      set({ isSaving: false, error: errorMessage(error, 'Failed to update supplier') });
      return null;
    }
  },

  archiveSupplier: async (organizationId, supplierId) => {
    set({ isSaving: true, error: null });
    try {
      await archiveSupplier(organizationId, supplierId);
      set((state) => ({
        suppliers: state.suppliers.filter((row) => row.id !== supplierId),
        total: Math.max(0, state.total - 1),
        selectedSupplier: state.selectedSupplier?.id === supplierId ? null : state.selectedSupplier,
        isSaving: false,
      }));
      return true;
    } catch (error) {
      set({ isSaving: false, error: errorMessage(error, 'Failed to archive supplier') });
      return false;
    }
  },

  shareSupplier: async (organizationId, supplierId, targetOwnerOrgId) => {
    set({ isSaving: true, error: null });
    try {
      const shared = await shareSupplier(organizationId, supplierId, targetOwnerOrgId);
      set({ isSaving: false });
      return shared;
    } catch (error) {
      set({ isSaving: false, error: errorMessage(error, 'Failed to share supplier') });
      return null;
    }
  },
}));
